import React, { useEffect, useRef } from 'react';
import './TravelInsuranceReminderModal.css';

interface TravelInsuranceReminderModalProps {
  isOpen: boolean;
  onClose: () => void;
  section?: 'Domestic' | 'Asia and Oceania' | 'WorldwideElite' | 'ShortSecure';
}

const TravelInsuranceReminderModal: React.FC<TravelInsuranceReminderModalProps> = ({
  isOpen,
  onClose,
  section
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isOpen) {
      // Focus the close button when modal opens
      closeButtonRef.current?.focus();
      
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      // Restore body scroll when modal closes
      document.body.style.overflow = 'unset';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  // Content mapping function based on section
  const getModalContent = () => {
    if (section === 'ShortSecure') {
      return {
        subtitle: 'Please read to avoid invalidating your travelsafe insurance.',
        items: [
          'Open to persons ages six (6) weeks old to seventy-five (75) years old.',
          'Travel using air transportation is not covered.',
          'Coverage shall commence at the time of departure of the Insured Person from the point of origin which may be the accommodation such as residence or employment, hotels, universities or schools, or the terminal wherein the transport vehicle to be used for the land or water trip will set off and ends on the early date of arrival at the point of origin, whichever occurs first.',
          'Coverage is allowed for travel using rented, public or company vehicles as well as private cars even when the Insured Person is driving.',
          'Accidents that occur outside the established route or town/cities in between the point of origin up to the place of destination are not covered.',
          'MAXIMUM PERIOD OF COVERAGE: Up to five (5) days per trip for ShortSecure Group and 15 days per trip for ShortSecure Individual'
        ]
      };
    }
    
    // Default content for all other sections (Domestic, WorldwideElite, Asia and Oceania)
    return {
      subtitle: 'Please read to avoid invalidating your travelsafe insurance.',
      items: [
        'The maximum period of insurance or coverage period for our Single Trip Plans is 180 days.',
        'Our Multi-Trip Plans cover an unlimited number of trips for each Policy year, provided that the maximum number of days for each business or pleasure trip to be covered under this insurance shall be 90 days.',
        'Please purchase Travelsafe Insurance on or before your scheduled departure date.',
        'Please ensure that you do not purchase multiple Travelsafe Insurance Plans with overlapping dates',
        <>
          The coverage period of your Travelsafe Insurance cannot be extended online. If you are already insured and have begun traveling, or if you have already returned to your point of origin earlier than the expiry date of your insurance period, please e-mail{' '}
          <a
            href="mailto:traveldirect_team@pacificcross.com.ph"
            className="travel-insurance-modal-email-link"
          >
            traveldirect_team@pacificcross.com.ph
          </a>
        </>,
        <>
          If you have purchased Travelsafe Insurance online and wish to change your coverage period, or any detail of your application, please e-mail{' '}
          <a
            href="mailto:traveldirect_team@pacificcross.com.ph"
            className="travel-insurance-modal-email-link"
          >
            traveldirect_team@pacificcross.com.ph
          </a>
        </>      

      ]
    };
  };

  const modalContent = getModalContent();

  if (!isOpen) return null;

  return (
    <div 
      className="travel-insurance-modal-backdrop" 
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <div className="travel-insurance-modal" ref={modalRef}>
        <div className="travel-insurance-modal-header">
          <div className="travel-insurance-modal-title-section">
            <div className="travel-insurance-modal-icon">
              🔔
            </div>
            <h2 id="modal-title" className="travel-insurance-modal-title">
              Reminder
            </h2>
          </div>
          <button
            ref={closeButtonRef}
            className="travel-insurance-modal-close"
            onClick={onClose}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>
        
        <div className="travel-insurance-modal-content">
          <p id="modal-description" className="travel-insurance-modal-subtitle">
            {modalContent.subtitle}
          </p>
          
          <ul className="travel-insurance-modal-list">
            {modalContent.items.map((item, index) => (
              <li key={index}>
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default TravelInsuranceReminderModal;